import { useEffect, useState } from 'react'
import type { Heading } from '../lib/page'
import { cx } from './ui'

/** Right-hand outline rail with a scroll spy over the doc's h2/h3 anchors. */
export function OnThisPage({ headings }: { headings: Heading[] }) {
  const [active, setActive] = useState<string | null>(headings[0]?.id ?? null)

  useEffect(() => {
    if (!headings.length) return

    const els = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => !!el)

    const observer = new IntersectionObserver(
      (entries) => {
        // Highlight the topmost heading currently inside the reading band.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) setActive(visible[0].target.id)
      },
      // Band sits just under the sticky header, ~40% down the viewport.
      { rootMargin: '-72px 0px -55% 0px', threshold: 0 },
    )

    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [headings])

  if (headings.length < 2) return null

  return (
    <nav aria-label="On this page" className="text-sm">
      <p className="mb-3 text-[11px] font-semibold tracking-wider text-subtle uppercase">
        On this page
      </p>
      <ul className="space-y-1 border-l border-line">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              onClick={(e) => {
                e.preventDefault()
                document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                history.replaceState(null, '', `#${h.id}`)
              }}
              className={cx(
                '-ml-px block border-l py-1 pr-2 transition-colors',
                h.level === 3 ? 'pl-6' : 'pl-3',
                active === h.id
                  ? 'border-l-accent font-medium text-accent-text'
                  : 'border-l-transparent text-muted hover:text-ink',
              )}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}
